import { FocusMonitor } from '@angular/cdk/a11y';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Optional,
  Output,
  Self,
  ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { ThemePalette } from '@angular/material/core';

import { TabulationTotalsOptions } from '../tabulation-table/models';

/**
 * Controls for tabulation options to show or hide total rows/columns.
 *
 * @author Will Davis <will.davis@mtna.us>
 */
@Component({
  selector: 'rds-tabulation-totals-options',
  template: `
    <div fxLayout="column">
      <h4 class="mat-caption">TOTAL ROWS/COLUMNS</h4>
      <mat-checkbox [color]="color" [disabled]="disabled" [checked]="value.showRowSubtotals" (change)="handleRowSubtotalsChange($event)"
        >Row Subtotals</mat-checkbox
      >
      <mat-checkbox [color]="color" [disabled]="disabled" [checked]="value.showRowTotals" (change)="handleRowTotalsChange($event)"
        >Row Totals</mat-checkbox
      >
      <mat-checkbox
        [color]="color"
        [disabled]="disabled"
        [checked]="value.showColumnSubtotals"
        (change)="handleColumnSubtotalsChange($event)"
        >Column Subtotals</mat-checkbox
      >
      <mat-checkbox [color]="color" [disabled]="disabled" [checked]="value.showColumnTotals" (change)="handleColumnTotalsChange($event)"
        >Column Totals</mat-checkbox
      >
    </div>
  `,
  styles: [
    `
      rds-tabulation-totals-options .mat-caption {
        text-transform: uppercase;
        font-weight: 500;
        margin-top: 0px;
        margin-bottom: 8px;
      }
    `,
  ],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RdsTabulationTotalsOptionsComponent implements ControlValueAccessor {
  /** Value for this form control */
  @Input()
  get value(): TabulationTotalsOptions {
    return this._value;
  }
  set value(value: TabulationTotalsOptions) {
    if (value !== this.value) {
      // Set the value or if null is provided, create a default option object
      this._value = value || new TabulationTotalsOptions();
      this._changeDetectorRef.markForCheck();
    }
  }
  private _value = new TabulationTotalsOptions();

  /** Theme color, defaults to primary */
  @Input() color: ThemePalette = 'primary';

  /** Whether the control is disabled */
  @Input()
  get disabled() {
    return this._disabled;
  }
  set disabled(value: any) {
    const newValue = coerceBooleanProperty(value);

    if (newValue !== this.disabled) {
      this._disabled = newValue;
      this._changeDetectorRef.markForCheck();
    }
  }
  private _disabled = false;

  /** Changed value of the form control */
  @Output() readonly change = new EventEmitter<TabulationTotalsOptions>();

  _onTouched: () => any = () => {};
  private _controlValueAccessorChangeFn: (value: any) => void = () => {};

  constructor(
    elementRef: ElementRef<HTMLElement>,
    @Optional() @Self() public ngControl: NgControl,
    private _changeDetectorRef: ChangeDetectorRef,
    private _focusMonitor: FocusMonitor
  ) {
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }

    this._focusMonitor.monitor(elementRef, true).subscribe((focusOrigin) => {
      if (!focusOrigin) {
        // When a focused element becomes disabled, the browser *immediately* fires a blur event.
        // Angular does not expect events to be raised during change detection, so any state change
        // (such as a form control's 'ng-touched') will cause a changed-after-checked error.
        // See https://github.com/angular/angular/issues/17793. To work around this, we defer
        // telling the form control it has been touched until the next tick.
        Promise.resolve().then(() => this._onTouched());
      }
    });
  }

  handleRowSubtotalsChange(event: MatCheckboxChange) {
    this.value = { ...this.value, showRowSubtotals: event.checked };
    this._emitChangeEvent();
  }

  handleRowTotalsChange(event: MatCheckboxChange) {
    this.value = { ...this.value, showRowTotals: event.checked };
    this._emitChangeEvent();
  }

  handleColumnSubtotalsChange(event: MatCheckboxChange) {
    this.value = { ...this.value, showColumnSubtotals: event.checked };
    this._emitChangeEvent();
  }

  handleColumnTotalsChange(event: MatCheckboxChange) {
    this.value = { ...this.value, showColumnTotals: event.checked };
    this._emitChangeEvent();
  }

  registerOnChange(fn: any): void {
    this._controlValueAccessorChangeFn = fn;
  }

  registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  writeValue(obj: any): void {
    this.value = obj;
  }

  _emitChangeEvent() {
    this._controlValueAccessorChangeFn(this.value);
    this.change.emit(this.value);
  }
}
